/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, Link, useParams } from 'react-router-dom';
import Pagination from 'react-js-pagination';
import Navbar from '../Navbar';
import Header from '../Header';
import baseUrl from '../BaseUrl';
import SignalPopup from '../heart-rate-sessions/SignalPopup';
import UserSegmentToggle from '../common/UserSegmentToggle';
import { useUserSegment, watchFilterValue } from '../../context/UserSegmentContext';
import { formatISODateToLocal, showTimeInSec } from '../../utils/utils';
import ExportButton from '../active-users/ExportButton';
import DateRangeBar, { fmt } from '../active-users/DateRangeBar';
import EmptyState from '../active-users/states/EmptyState';
import ErrorState from '../active-users/states/ErrorState';
import TableSkeleton from '../active-users/states/TableSkeleton';
import PauseHistorySummaryCards from './PauseHistorySummaryCards';
import '../active-users/active-users.css';

const LIMIT = 20;
const deviceLabel = (v) => (v === 'true' ? 'Watch' : v === 'false' ? 'Mobile' : 'Other');
const EXPORT_COLUMNS = [
  { key: 'id', label: 'No' },
  { key: 'name', label: 'Name' },
  { key: 'breath_perform_time', label: 'Perform time (ms)' },
  { key: 'breath_time', label: 'Breath time (ms)' },
  { key: 'breath_type', label: 'Breath type' },
  { key: 'breath_pattern', label: 'Breath pattern' },
  { key: 'breath_cycle', label: 'Breath cycle' },
  { key: 'breath_cycle_length', label: 'Breath cycle length' },
  { key: 'breath_inhale', label: 'Inhale (ms)' },
  { key: 'breath_exhale', label: 'Exhale (ms)' },
  { key: 'breath_hold', label: 'Hold (ms)' },
  { key: 'is_from_watch', label: 'From Device' },
  { key: 'timestamp_utc', label: 'Created' },
];

const BreathList = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const userId = id ? atob(id) : '';
  const { segment } = useUserSegment();                 // global watch/without/all (feature 023)
  const userSegment = watchFilterValue(segment);        // 'with_watch' | 'without_watch' | ''

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [rows, setRows] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [displayFrom, setDisplayFrom] = useState(null);
  const [displayTo, setDisplayTo] = useState(null);
  const [preset, setPreset] = useState(null);

  const [breathTypes, setBreathTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [watchFilter, setWatchFilter] = useState('');

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupData, setPopupData] = useState(null);

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  useEffect(() => {
    // No default date window — the detail page opens all-time; the user can
    // narrow with the date range bar.
    refreshToken();
    getTypes();
  }, []);

  useEffect(() => {
    if (token) loadData(1, fromDate, toDate, selectedType, watchFilter);
  }, [token]);

  // Re-fetch when the global watch segment changes (skip initial mount).
  const didMountSeg = useRef(false);
  useEffect(() => {
    if (!didMountSeg.current) { didMountSeg.current = true; return; }
    if (!token) return;
    setPage(1);
    loadData(1, fromDate, toDate, selectedType, watchFilter);
  }, [segment]);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${token}` } });
  const scope = () => (userId ? `&user_id=${userId}` : '');
  const device = (w) => (w ? `&watch_filter=${w}` : '');
  const seg = () => (userSegment ? `&user_segment=${userSegment}` : '');

  const getTypes = async () => {
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/breath-types`, authHeaders());
      const raw = res?.data?.data?.[0]?.breath_types;
      if (raw) setBreathTypes(JSON.parse(raw));
    } catch { /* non-blocking */ }
  };

  const loadData = async (p, f, t, type, w) => {
    setLoading(true); setError('');
    const filters = `from_date=${f}&to_date=${t}&breath_type=${type}${device(w)}${scope()}${seg()}`;
    try {
      const [list, sum] = await Promise.all([
        axiosJWT.get(`${baseUrl}/admin/api/breath?page=${p}&limit=${LIMIT}&${filters}`, authHeaders()),
        axiosJWT.get(`${baseUrl}/admin/api/breath-summary?${filters}`, authHeaders()),
      ]);
      setRows(list.data.data || []);
      setTotalItem(list.data.totalItem || 0);
      setSummary(sum.data.data || null);
    } catch (err) {
      if (err?.response?.status === 401) setError('auth'); else setError('load');
    } finally { setLoading(false); }
  };

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
    loadData(pageNumber, fromDate, toDate, selectedType, watchFilter);
  };
  const handleFromChange = (date) => { setPreset('custom'); setDisplayFrom(date); setFromDate(date ? fmt(date) : ''); };
  const handleToChange = (date) => { setPreset('custom'); setDisplayTo(date); setToDate(date ? fmt(date) : ''); };
  const handlePreset = (key, fromStr, toStr) => {
    setPreset(key); setFromDate(fromStr); setToDate(toStr);
    setDisplayFrom(fromStr ? new Date(fromStr) : null); setDisplayTo(toStr ? new Date(toStr) : null);
    setPage(1); loadData(1, fromStr, toStr, selectedType, watchFilter);
  };
  const handleSearch = () => { setPage(1); loadData(1, fromDate, toDate, selectedType, watchFilter); };
  const handleTypeChange = (e) => { const v = e.target.value; setSelectedType(v); setPage(1); loadData(1, fromDate, toDate, v, watchFilter); };
  const handleDeviceChange = (e) => { const v = e.target.value; setWatchFilter(v); setPage(1); loadData(1, fromDate, toDate, selectedType, v); };
  const clearFilter = () => {
    setFromDate(''); setToDate(''); setDisplayFrom(null); setDisplayTo(null);
    setPreset(null); setSelectedType(''); setWatchFilter(''); setPage(1);
    loadData(1, '', '', '', '');
  };

  const openSignal = (row) => { setPopupData(row.dump_hr_details); setIsPopupOpen(true); };
  const closePopup = () => { setIsPopupOpen(false); setPopupData(null); };

  const fetchExport = async () => {
    const res = await axiosJWT.get(
      `${baseUrl}/admin/api/breath-export?from_date=${fromDate}&to_date=${toDate}&breath_type=${selectedType}${device(watchFilter)}${scope()}${seg()}`,
      authHeaders());
    return res.data.data || [];
  };

  const hasFilters = !!(selectedType || watchFilter || fromDate || toDate);
  const dmy = (s) => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };
  const dur = (v) => (v == null ? '—' : showTimeInSec(v));

  const summaryCards = summary ? [
    { label: 'Total Breath', value: Number(summary.total_sessions || 0).toLocaleString() },
    { label: 'Avg session duration', value: dur(summary.avg_duration) },
    { label: 'Distinct active days', value: Number(summary.active_days || 0).toLocaleString() },
    { label: 'From Device', parts: [
      { label: 'Watch', value: Number(summary.watch_count || 0).toLocaleString() },
      { label: 'Mobile', value: Number(summary.mobile_count || 0).toLocaleString() },
      { label: 'Other', value: Number(summary.other_count || 0).toLocaleString() },
    ] },
  ] : [];

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Activity › Pauses › <b>Breathing history</b></span>
            <span className="au-route">/breath-list</span>
            <span className="au-spacer" />
            <UserSegmentToggle />
            <span className="au-back"><Link to="/user-pause-count">← Back</Link></span>
          </div>
          <h1 className="au-title">Breathing history</h1>

          {error === 'auth' ? (
            <ErrorState title="Your session expired" message="Please sign in again to continue." onRetry={() => navigate('/')} />
          ) : error === 'load' ? (
            <ErrorState message="We couldn’t load the breathing history. Please try again." onRetry={() => loadData(page, fromDate, toDate, selectedType, watchFilter)} />
          ) : (
            <>
              <PauseHistorySummaryCards cards={summaryCards} />

              <DateRangeBar
                leading={(
                  <>
                    <select className="au-select" value={selectedType} onChange={handleTypeChange} aria-label="Filter by breath type">
                      <option value="">All breath types</option>
                      {breathTypes.map((t, i) => <option key={i} value={t}>{t}</option>)}
                    </select>
                    <select className="au-select" value={watchFilter} onChange={handleDeviceChange} aria-label="Filter by device">
                      <option value="">All devices</option>
                      <option value="from_watch">Watch</option>
                      <option value="from_mobile">Mobile</option>
                      <option value="from_other">Other</option>
                    </select>
                  </>
                )}
                preset={preset}
                displayFrom={displayFrom}
                displayTo={displayTo}
                hasRange={hasFilters}
                onPreset={handlePreset}
                onFromChange={handleFromChange}
                onToChange={handleToChange}
                onSearch={handleSearch}
                onClear={clearFilter}
              />
              <p className="au-range-note" data-testid="pause-range-note">
                The date range filters records by their <b>activity date (UTC)</b> — the “Created” column (the moment each breath was recorded).{' '}
                {(fromDate || toDate)
                  ? <>Showing <b>{dmy(fromDate) || '…'}</b> – <b>{dmy(toDate) || '…'}</b>.</>
                  : 'Showing all records (no date filter).'}
              </p>

              {loading ? (
                <div className="au-panel"><TableSkeleton rows={8} /></div>
              ) : rows.length === 0 ? (
                <EmptyState title="No records in this range" hasFilters={hasFilters} onClear={clearFilter}
                  message="No breathing records match the current filters." />
              ) : (
                <div className="au-panel">
                  <div className="au-panel__head">
                    <h4 className="au-panel__title">Breathing records</h4>
                    <span className="au-spacer" />
                    <ExportButton columns={EXPORT_COLUMNS} fetchRows={fetchExport} filename="breath-history.csv" />
                  </div>
                  <div className="au-table-wrap">
                    <table className="au-table au-mau-table" data-testid="breath-table">
                      <thead>
                        <tr>
                          <th>No</th><th>Name</th><th>Perform time</th><th>Breath time</th><th>Breath type</th>
                          <th>Breath pattern</th><th>Breath cycle</th><th>Breath cycle length</th>
                          <th>Inhale</th><th>Exhale</th><th>Hold</th><th>From Device</th><th>Created</th><th>Signal Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr key={r.id}>
                            <td data-label="No" className="tnum">{r.id}</td>
                            <td data-label="Name" className="au-ellipsis">{r.name}</td>
                            <td data-label="Perform time" className="tnum">{showTimeInSec(r.breath_perform_time)}</td>
                            <td data-label="Breath time" className="tnum">{showTimeInSec(r.breath_time)}</td>
                            <td data-label="Breath type">{r.breath_type}</td>
                            <td data-label="Breath pattern">{r.breath_pattern}</td>
                            <td data-label="Breath cycle" className="tnum">{r.breath_cycle}</td>
                            <td data-label="Breath cycle length" className="tnum">{r.breath_cycle_length}</td>
                            <td data-label="Inhale" className="tnum">{showTimeInSec(r.breath_inhale)}</td>
                            <td data-label="Exhale" className="tnum">{showTimeInSec(r.breath_exhale)}</td>
                            <td data-label="Hold" className="tnum">{showTimeInSec(r.breath_hold)}</td>
                            <td data-label="From Device">{deviceLabel(r.is_from_watch)}</td>
                            <td data-label="Created">{formatISODateToLocal(r.timestamp_utc)}</td>
                            <td data-label="Signal Details">
                              <button type="button" className="au-link" aria-label={`View signal details for record ${r.id}`} onClick={() => openSignal(r)}>
                                <img src={`${process.env.PUBLIC_URL}/images/view.png`} alt="" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalItem > LIMIT && (
                    <Pagination activePage={page} itemsCountPerPage={LIMIT} totalItemsCount={totalItem} pageRangeDisplayed={5} onChange={handlePageChange} />
                  )}
                </div>
              )}

              {isPopupOpen && <SignalPopup data={popupData} onClose={closePopup} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BreathList;
